import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import {
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    Heading,
    useOverlayClose,
} from '@gitbook/spine-react';

import { ContentKitButton } from './ContentKitButton';
import { ensureStackElements } from './ContentKitStack';
import type { ContentKitElementProps } from './types';

/**
 * Render a modal and dispatch an action when closed.
 */
export function ContentKitModal(
    props: ContentKitElementProps<api.ContentKitModal>
): React.ReactElement {
    const { element, renderElements } = props;

    const onClose = useOverlayClose();
    const children = renderElements('block', ensureStackElements('vstack', element.children));
    const subtitle = element.subtitle
        ? renderElements('inline', ensureStackElements('hstack', element.subtitle, 'start'))
        : undefined;

    if (!element.title && !element.submit) {
        return <>{children}</>;
    }

    const size = (() => {
        switch (element.size) {
            case 'fullscreen':
                return 'full-screen';
            case 'xlarge':
                return { width: 896 };
            default:
                return { width: 600 };
        }
    })();

    return (
        <Dialog size={size}>
            <DialogHeader>
                <Heading>{element.title || ''}</Heading>
                <Description>{subtitle}</Description>
            </DialogHeader>
            <DialogBody>{children}</DialogBody>
            <DialogFooter>
                <DialogCloseButton onPress={onClose} />
                {element.submit ? (
                    <ContentKitButton
                        {...props}
                        element={element.submit}
                        buttonDefaultKind="primary"
                        buttonProps={{
                            size: 'large',
                        }}
                    />
                ) : null}
            </DialogFooter>
        </Dialog>
    );
}
